import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { PrimaryButton } from 'Core/components/Button';
import { AlertError } from 'Core/components/Alert';
import useConfirmPayment from 'Core/hooks/queries/stripe/useConfirmPayment';
import { stripeFor } from 'Core/utils/stripe';
import { messageOf, savePayment } from 'Modules/checkout/api';
import { checkoutUrl } from 'Modules/checkout/hooks/useStripeReturn';
import { billingDefaultsOf, fullNameOf } from 'Modules/checkout/components/SaveMethod';

const SUBMIT = {
  CARD: { key: 'checkout.payNow.CARD.submit', label: 'Jetzt bezahlen' },
  SEPA: { key: 'checkout.payNow.SEPA.submit', label: 'Jetzt per Lastschrift bezahlen' },
};

function PayNowForm({ method, customer, onDone }) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const confirm = useConfirmPayment();
  const submitLabel = SUBMIT[method] || SUBMIT.CARD;
  const [holder, setHolder] = useState(fullNameOf(customer));
  const [saveMethod, setSaveMethod] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const askHolder = method === 'CARD';
  const options = billingDefaultsOf(customer, !askHolder);

  const submit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || (askHolder && !holder.trim())) {
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      await savePayment({ save_method: askHolder && saveMethod });
    } catch (error) {
      setSaveError(error);
      setSaving(false);
      return;
    }
    setSaving(false);
    const billingDetails = askHolder ? { name: holder.trim() } : null;
    confirm.mutate({ stripe, elements, returnUrl: checkoutUrl(), billingDetails }, { onSuccess: onDone });
  };
  const error = saveError || (confirm.isError ? confirm.error : null);

  return (
    <form onSubmit={submit} data-testid="pay-now">
      {askHolder && (
        <label className="block mb-3">
          <span className="block text-sm text-gray-700 mb-1">
            {t('checkout.saveMethod.CARD.holder', 'Name des Karteninhabers')}
          </span>
          <input
            type="text"
            autoComplete="cc-name"
            value={holder}
            onChange={(event) => setHolder(event.target.value)}
            data-testid="card-holder"
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          />
        </label>
      )}
      <PaymentElement options={options} />
      {askHolder && (
        <label className="flex items-center gap-2 text-sm text-gray-700 mt-4">
          <input
            type="checkbox"
            checked={saveMethod}
            onChange={(event) => setSaveMethod(event.target.checked)}
            data-testid="save-method"
          />
          {t('checkout.payNow.CARD.save', 'Karte für künftige Zahlungen speichern')}
        </label>
      )}
      {error && (
        <div className="mt-3" data-testid="stripe-error">
          <AlertError>{messageOf(error)}</AlertError>
        </div>
      )}
      <div className="flex justify-end mt-4">
        <PrimaryButton
          type="submit"
          data-testid="pay-now-submit"
          disabled={!stripe || !elements || saving || confirm.isLoading || (askHolder && !holder.trim())}
        >
          {t(submitLabel.key, submitLabel.label)}
        </PrimaryButton>
      </div>
    </form>
  );
}

export default function PayNow({ method, customer, clientSecret, publishableKey, onDone }) {
  return (
    <Elements
      key={clientSecret}
      stripe={stripeFor(publishableKey)}
      options={{ clientSecret, locale: 'de' }}
    >
      <PayNowForm method={method} customer={customer} onDone={onDone} />
    </Elements>
  );
}
